// Polish-pass and bug-category constants, shared by the client and the server.
//
// This module stays dependency-light because both browser and server callers consume it.
import { z } from 'zod'
import { BUG_CATEGORIES, type BugCategory } from '../../../src/lib/bug-management/categories.ts'
import { DEFAULT_ENABLED_PASSES } from '../../../src/guidance/polish/default-enabled-passes.ts'

export { BUG_CATEGORIES, type BugCategory }

export const BugCategorySchema = z.enum(BUG_CATEGORIES)

// Every polish pass files bugs; testing is a bug category, not a polish pass.
export const PolishPassTypeSchema = BugCategorySchema.exclude(['testing'])
export type PolishPassType = z.infer<typeof PolishPassTypeSchema>

// `.options`, not a .filter(): keeps the readonly tuple type, so callers cannot push/assign into it.
export const POLISH_PASS_TYPES = PolishPassTypeSchema.options

// openapi.json's enum. APPEND-ONLY: reordering rewrites a public contract.
export const PUBLISHED_PASS_ORDER = [
  'network-performance',
  'react-rendering',
  'layout-shift',
  'accessibility',
  'glitches',
  'user-experience',
  'seo',
  'ui-details',
  'security',
] as const satisfies readonly PolishPassType[]

// Compile-time check that every pass is published; the error names the missing one.
type MissingFromPublished = Exclude<PolishPassType, (typeof PUBLISHED_PASS_ORDER)[number]>
const _allPassesPublished: [MissingFromPublished] extends [never] ? true : MissingFromPublished = true
void _allPassesPublished

// Public re-export for the existing client/server import path. The literal lives in the dependency-free
// guidance module so runtime guidance bundles consume the same definition instead of maintaining copies.
export { DEFAULT_ENABLED_PASSES }

// Compile-time checks that every default is a real pass and that adding a new pass does not silently make
// it a default. The latter remains an explicit product decision made in default-enabled-passes.ts.
type InvalidDefault = Exclude<(typeof DEFAULT_ENABLED_PASSES)[number], PolishPassType>
const _defaultsArePasses: [InvalidDefault] extends [never] ? true : InvalidDefault = true
void _defaultsArePasses

// A DELTA to an enabled-pass set: turn these on, turn those off, leave every other pass as it is.
//
// The full-set form (`enabled_polish_passes`) makes a caller who wants ONE extra pass restate the whole
// default set, and that copy then goes stale. It already did: QA Benchmark's "UI Details" app setting sent
// a hardcoded "defaults plus ui-details" list, so turning it on silently DISABLED react-rendering and
// accessibility after the defaults gained them. A delta names only what the caller has an opinion about,
// so there is no copy to go stale.
export interface PolishPassDelta {
  add?: readonly PolishPassType[]
  remove?: readonly PolishPassType[]
}

// Apply a delta to `base`. Removals first, then additions appended in the order given — so the surviving
// members keep their existing order (the column's order is itself a stored value; see
// DEFAULT_ENABLED_PASSES) and only the new ones land at the end. Idempotent: adding an already-enabled
// pass and removing an absent one are both no-ops, and the result is deduplicated. A pass in BOTH lists
// ends up ENABLED (removals are applied first), but callers should reject that as a mistake rather than
// rely on the order — the v1 API does.
export function applyPolishPassDelta(
  base: readonly PolishPassType[],
  delta: PolishPassDelta,
): PolishPassType[] {
  const removed = new Set<string>(delta.remove ?? [])
  const out: PolishPassType[] = []
  const seen = new Set<string>()
  for (const pass of base) {
    if (removed.has(pass) || seen.has(pass)) continue
    seen.add(pass)
    out.push(pass)
  }
  for (const pass of delta.add ?? []) {
    if (seen.has(pass)) continue
    seen.add(pass)
    out.push(pass)
  }
  return out
}

export const BUG_CATEGORY_LABELS: Readonly<Record<BugCategory, string>> = {
  testing: 'Testing',
  'network-performance': 'Network Performance',
  'react-rendering': 'React Rendering',
  'layout-shift': 'Layout Shift',
  glitches: 'Glitches',
  'user-experience': 'User Experience',
  accessibility: 'Accessibility',
  'ui-details': 'UI Details',
  seo: 'SEO',
  security: 'Security',
}

export function bugCategoryLabel(category: string): string {
  if (category === 'detail-journeys') return BUG_CATEGORY_LABELS['ui-details']
  const known = BugCategorySchema.safeParse(category)
  return known.success ? BUG_CATEGORY_LABELS[known.data] : category.replace(/-/g, ' ')
}
